import { computed, toValue } from 'vue';
import type { MaybeRefOrGetter } from '@vue/reactivity';
import { Features } from '@shared/features';
import { useShared, useAuthenticated } from '@othercommerce/foundation';
import type { StockMeta } from '@storefront/resources';

type Variant = {
  id: number;
  stockOnOrder: boolean;
  stockAvailable: string;
  stockMeta: StockMeta;
  disableSalesOverSupply: boolean;
}

type Source = MaybeRefOrGetter<Variant | undefined> | undefined;

export function useVariantStock(source?: Source) {
  const shared = useShared();
  const authenticated = useAuthenticated();

  const canOrderOverSupply = computed(() => {
    if (Features.ordersOverSupply.isEnabled && !toValue(source)?.disableSalesOverSupply) {
      if (Features.ordersOverSupplyForBusinessOnly.isEnabled) {
        if (authenticated.value && authenticated.value.b2b) {
          return true;
        }
      } else {
        return true;
      }
    }

    return false;
  });

  const stockOnOrder = computed(() => {
    return toValue(source)?.stockOnOrder || false;
  });

  const amountInStock = computed(() => {
    return toValue(source)?.stockAvailable || 0;
  });

  const amountInCart = computed(() => {
    const cart = shared.value.cart;
    const variant = toValue(source);

    if (!cart || !variant) {
      return 0;
    }

    const item = cart.items.find((item) => {
      return item.variantId === variant!.id;
    });

    if (!item) {
      return 0;
    }

    return item.amount;
  });

  const amountAvailable = computed(() => {
    if (stockOnOrder.value || canOrderOverSupply.value) {
      return Number.POSITIVE_INFINITY;
    }

    return Number(amountInStock.value) - Number(amountInCart.value);
  });

  const maxAmountAvailable = computed(() => {
    if (stockOnOrder.value || canOrderOverSupply.value) {
      return Number.POSITIVE_INFINITY;
    }

    return Number(amountInStock.value);
  });

  const inStock = computed(() => {
    if (stockOnOrder.value) {
      return true;
    }

    return Number(amountInStock.value) > 0;
  });

  const available = computed(() => {
    if (stockOnOrder.value) {
      return true;
    }

    if (canOrderOverSupply.value) {
      return true;
    }

    if (Number(amountInStock.value) > 0) {
      return Number(amountInCart.value) < Number(amountInStock.value);
    }

    return false;
  });

  return {
    stockOnOrder,
    amountInStock,
    amountInCart,
    amountAvailable,
    maxAmountAvailable,
    inStock,
    available,
  };
}
